import type { OptionType } from "components/inputs";
import { useMemo } from "react";
import type { UserType } from "services/users-api";
import type {
  AdjusterType,
  ConstantTypes,
  PaginatedResponse,
  RREDataType,
} from "types/DataTypes";
import { RELATIVE_DATE_OPTIONS } from "utils/date-utils";

export type FilterType = {
  label: string;
  name: string;
  options: OptionType[];
  addNullOption?: boolean;
  type?: "select" | "date";
};

export type BinaryFilterType = {
  label: string;
  name: string;
};

export type DateRangeFilterType = {
  label: string;
  nameFrom: string;
  nameTo: string;
};

export type SelectFilterType = {
  selects: FilterType[];
  binary: BinaryFilterType[];
  dateRanges: DateRangeFilterType[];
};

type ApiResultsType = {
  users?: PaginatedResponse<UserType>;
  adjusters?: PaginatedResponse<AdjusterType>;
  rres?: PaginatedResponse<RREDataType>;
};

export const useSearchFilters = (
  filterType: string,
  constants: ConstantTypes,
  apiResults: ApiResultsType,
): SelectFilterType => {
  const filters = useMemo(() => {
    const defaultMap = {
      claims: {
        selects: [
          {
            label: "Next Action",
            name: "next_scheduled_action",
            options: constants?.EXTENDED_NEXT_SCHEDULED_ACTIONS,
          },
          {
            label: "Adjuster",
            name: "adjuster",
            options: apiResults?.adjusters?.results?.map((a: AdjusterType) => ({
              label: a.name,
              value: a.name,
            })),
            addNullOption: true,
          },
          {
            label: "RRE",
            name: "rre",
            options: apiResults?.rres?.results?.map((a: RREDataType) => ({
              label: a.name,
              value: a.id,
            })),
            addNullOption: true,
          },
          {
            label: "Last Claim File Disposition",
            name: "last_reporting_disposition_codes_received",
            options: constants?.DISPOSITION_CODES,
          },
        ],
        binary: [
          {
            label: "Has future medicare entitlement?",
            name: "medicare_entitlement_date_too_far_out_on_last_save",
          },
          {
            label: "Is Medicare Beneficiary?",
            name: "is_medicare_beneficiary",
          },
          {
            label: "Has Reporting Errors?",
            name: "has_cms_errors_to_resolve",
          },
        ],
        dateRanges: [],
      },
      rres: {
        selects: [
          {
            label: "Client",
            name: "client",
            options: (() => {
              // extract unique clients from rres
              const uniqueClients = new Map<number, string>();
              apiResults?.rres?.results?.forEach((rre: RREDataType) => {
                if (
                  rre.rre_profile?.client_id &&
                  rre.rre_profile?.client_name
                ) {
                  uniqueClients.set(
                    rre.rre_profile.client_id,
                    rre.rre_profile.client_name,
                  );
                }
              });
              return Array.from(uniqueClients.entries()).map(
                ([clientId, clientName]) => ({
                  label: clientName,
                  value: clientId,
                }),
              );
            })(),
            addNullOption: true,
          },
          {
            label: "Reporter Type",
            name: "reporter_type",
            options: constants?.RRE_REPORTER_TYPES,
          },
          {
            label: "Line of Business",
            name: "lines_of_business",
            options: constants?.PLAN_INSURANCE_TYPE,
          },
        ],
        binary: [],
        dateRanges: [],
      },
      "your-tasks": {
        selects: [
          {
            label: "Product Type",
            name: "product_type",
            options: constants?.SERVICE_TYPE,
          },
          {
            label: "File Status",
            name: "file_status",
            options: constants?.FILE_STATUS,
          },
          {
            label: "Completed Tasks",
            name: "completed_tasks_filter",
            options: RELATIVE_DATE_OPTIONS,
          },
        ],
        binary: [
          {
            label: "Rush",
            name: "is_rush",
          },
          {
            label: "3 Day Rush",
            name: "is_3_day_rush",
          },
          {
            label: "24 Hour Rush",
            name: "is_24_hour_rush",
          },
        ],
        dateRanges: [],
      },
      files: {
        selects: [
          {
            label: "Service Type",
            name: "service",
            options: constants?.SERVICE_TYPE,
          },
          {
            label: "File Status",
            name: "status",
            options: constants?.FILE_STATUS,
          },
          {
            label: "Exclude Status",
            name: "not_status",
            options: constants?.FILE_STATUS,
          },
          {
            label: "Assignee",
            name: "assignee",
            options: apiResults?.users?.results.map((u: UserType) => ({
              label: `${u.first_name} ${u.last_name}` || u.email,
              value: u.id,
            })),
          },
        ],
        binary: [
          {
            label: "Rush",
            name: "is_rush",
          },
          {
            label: "3 Day Rush",
            name: "is_3_day_rush",
          },
          {
            label: "24 Hour Rush",
            name: "is_24_hour_rush",
          },
        ],
        dateRanges: [
          {
            label: "Created Date",
            nameFrom: "created_gte",
            nameTo: "created_lte",
          },
          {
            label: "Opened Date",
            nameFrom: "datetime_opened_gte",
            nameTo: "datetime_opened_lte",
          },
          {
            label: "Closed Date",
            nameFrom: "datetime_closed_gte",
            nameTo: "datetime_closed_lte",
          },
        ],
      },
      operations: {
        selects: [
          {
            label: "AR Screening Document Type",
            name: "ar_screening_document_type",
            options: constants?.AR_SCREENING_DOCUMENT_TYPES || [],
          },
        ],
        binary: [
          {
            label: "CMS Submission Document",
            name: "is_cms_submission_document",
          },
        ],
        dateRanges: [],
      },
      documents: {
        selects: [
          {
            label: "Document Type",
            name: "type",
            options: constants?.DOCUMENT_TYPES || [],
          },
          {
            label: "Service Type",
            name: "service_type",
            options: (() => {
              const serviceTypes = constants?.SERVICE_TYPE || [];

              // filter out the default "-- Select --" option
              const filteredServices = serviceTypes.filter(
                (opt: OptionType) =>
                  opt.value !== "" && opt.label !== "-- Select --",
              );

              // add Future Medical catch-all option and sort alphabetically
              const allServices = [
                { label: "Future Medical *", value: "FUTURE_MEDICAL" },
                ...filteredServices,
              ];

              return allServices.sort((a, b) => a.label.localeCompare(b.label));
            })(),
            addNullOption: true,
          },
          {
            label: "AR Screening Document Type",
            name: "ar_screening_document_type",
            options: constants?.AR_SCREENING_DOCUMENT_TYPES || [],
          },
        ],
        binary: [
          {
            label: "CMS Submission Document",
            name: "is_cms_submission_document",
          },
        ],
        dateRanges: [],
      },
      "": {
        selects: [
          {
            label: "Service Type",
            name: "service_type",
            options: constants?.SERVICE_TYPE,
          },
        ],
        binary: [],
        dateRanges: [],
      },
    };

    const selectedFilters = defaultMap[filterType] || defaultMap[""];

    return selectedFilters;
  }, [filterType, constants, apiResults]);

  return filters;
};
